import React from "react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { AlertCircle, RotateCw } from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";

export function EcosuiteErrorBoundary({
  children,
  title = "An error occurred",
  description = "An unexpected error occurred. Please try again or contact support if the problem persists.",
  className,
}: {
  children: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
}) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={({ error, resetErrorBoundary }) => (
            <div
              className={cn(
                "flex min-h-[400px] items-center justify-center p-4",
                className,
              )}
            >
              <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                  <div className="mb-2 flex justify-center">
                    <div className="bg-destructive/10 rounded-full p-3">
                      <AlertCircle className="text-destructive h-6 w-6" />
                    </div>
                  </div>
                  <CardTitle className="text-xl font-semibold">
                    {title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground mt-2">
                    {description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 rounded-lg p-4 text-sm">
                    <p className="text-muted-foreground whitespace-pre-wrap break-all font-mono">
                      Error Message: {error.message}
                    </p>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-center">
                  <Button
                    onClick={() => resetErrorBoundary()}
                    className="gap-2"
                  >
                    <RotateCw className="h-4 w-4" />
                    Try Again
                  </Button>
                </CardFooter>
              </Card>
            </div>
          )}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
